import React, { useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarEvent } from "@/pages/scheduling/types";
import TotalWorkCalendar from "@/pages/scheduling/components/TotalWorkCalendar";
import { format, addWeeks, subWeeks, differenceInMinutes, differenceInHours, addDays, endOfDay, startOfDay } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Filter, UserRound, ChevronLeft, ChevronRight, Calendar, Phone, User, FileDigit, Clock, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import GuardSelect from "@/pages/scheduling/components/GuardSelect";
import { useGuards } from "@/pages/scheduling/hooks/useGuards";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { calculateWeekBoundaries } from "@/pages/schedule-alerts/services/utils/dateUtils";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Client, Team } from "../types";
import { exportClientSchedulesToExcel } from "../services/exportService";

interface ClientScheduleViewProps {
  clientId: string;
  teamId?: string; // Optional teamId for direct team viewing
  showAllTeams?: boolean; // Added this prop for showing all teams schedules
}

interface GuardContact {
  id: string;
  name: string;
  mobile_phone?: string;
  email?: string;
  afm?: string; // Added AFM to guard contact
  code?: string; // Added code to guard contact
}

export const ClientScheduleView = ({ clientId, teamId, showAllTeams = false }: ClientScheduleViewProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [clientTeams, setClientTeams] = useState<Team[]>([]);
  const [allShifts, setAllShifts] = useState<CalendarEvent[]>([]);
  const [filteredShifts, setFilteredShifts] = useState<CalendarEvent[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("all");
  const [selectedGuardId, setSelectedGuardId] = useState<string>("all");
  const [guardNames, setGuardNames] = useState<Record<string, string>>({});
  const [guardContacts, setGuardContacts] = useState<Record<string, GuardContact>>({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<"calendar" | "list">("calendar");
  const { toast } = useToast();
  const { isAdmin, roles } = useUserRoles();
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined
  });
  
  const { availableGuards: teamGuards = [], allGuards: allTeamGuards = [], isLoading: guardsLoading } = useGuards(
    selectedTeamId !== "all" ? selectedTeamId : null
  );
  
  const calendarDateRange = {
    start: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - currentDate.getDay() + 1),
    end: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + (7 - currentDate.getDay()))
  };

  const goToPreviousWeek = () => {
    setCurrentDate(prevDate => subWeeks(prevDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(prevDate => addWeeks(prevDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const { weekStartFormatted, weekEndFormatted } = calculateWeekBoundaries(currentDate);

  const formatShiftDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const totalMinutes = differenceInMinutes(end, start);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const fetchTeamSchedulesWithDateRange = async (teamIds: string[], startDate: Date, endDate: Date) => {
    try {
      setIsLoading(true);
      
      const { data: guards } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, mobile_phone, email, afm, code')
        .eq('Role', 'guard');
      
      const guardsMap: Record<string, string> = {};
      const contactsMap: Record<string, GuardContact> = {};
      
      guards?.forEach(guard => {
        guardsMap[guard.id] = guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim() || 'Unknown';
        contactsMap[guard.id] = {
          id: guard.id,
          name: guardsMap[guard.id],
          mobile_phone: guard.mobile_phone,
          email: guard.email,
          afm: guard.afm,
          code: guard.code
        };
      });
      
      const startDateWithTime = startOfDay(startDate);
      const endDateWithTime = endOfDay(addDays(endDate, 1));
      
      console.log(`Fetching schedules between ${startDateWithTime.toISOString()} and ${endDateWithTime.toISOString()}`);
      
      const allTeamShifts: CalendarEvent[] = [];
      
      for (const teamId of teamIds) {
        const { data: schedules, error: schedulesError } = await supabase
          .from('team_schedules')
          .select('*')
          .eq('team_id', teamId)
          .gte('start_date', startDateWithTime.toISOString())
          .lt('start_date', endDateWithTime.toISOString())
          .not('title', 'ilike', '%ΡΕΠΟ%')
          .not('title', 'ilike', '%ρεπο%')
          .not('title', 'ilike', '%ΜΕ%')
          .not('title', 'ilike', '%με%');
          
        if (schedulesError) {
          console.error('Error fetching team schedules:', schedulesError);
          continue;
        }
        
        if (schedules && schedules.length > 0) {
          const { data: teamData } = await supabase
            .from('teams')
            .select('name')
            .eq('id', teamId)
            .single();
            
          const teamName = teamData?.name || 'Unknown Team';
          
          const formattedShifts = schedules.map(schedule => {
            let guardNames: string[] = [];
            if (schedule.assigned_guards && Array.isArray(schedule.assigned_guards)) {
              guardNames = schedule.assigned_guards.map(id => guardsMap[id] || 'Unknown');
            }
            
            return {
              ...schedule,
              title: `[${teamName}] ${schedule.title}`,
              color: 'primary',
              guard_names: guardNames,
              team_id: teamId,
              team_name: teamName
            };
          });
          
          allTeamShifts.push(...formattedShifts);
        }
      }
      
      console.log(`Found ${allTeamShifts.length} shifts within date range`);
      setAllShifts(allTeamShifts);
      setFilteredShifts(allTeamShifts);
    } catch (error: any) {
      console.error('Error fetching team schedules with date range:', error);
      toast({
        title: "Error loading schedules",
        description: error.message || "Failed to load team schedules",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeChange = (range: { from: Date | undefined; to: Date | undefined }) => {
    setDateRange(range);
    if (range.from && range.to) {
      fetchTeamSchedulesWithDateRange([...clientTeams.map(t => t.id)], range.from, range.to);
    }
  };

  const handleExportToExcel = () => {
    if (filteredShifts.length === 0) {
      toast({
        title: "Nothing to export",
        description: "No schedules match the current filters.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const teamNameForExport = selectedTeamId !== "all" 
        ? clientTeams.find(team => team.id === selectedTeamId)?.name 
        : undefined;
      
      exportClientSchedulesToExcel(
        filteredShifts,
        dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : undefined,
        teamNameForExport
      );
      
      toast({
        title: "Export successful",
        description: `${filteredShifts.length} schedules exported to Excel.`,
        variant: "default"
      });
    } catch (error: any) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description: error.message || "Failed to export schedules to Excel",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    const fetchClientTeams = async () => {
      try {
        if (!clientId && !teamId && !showAllTeams) {
          console.log('No client ID, team ID, or showAllTeams flag provided');
          setIsLoading(false);
          return;
        }
        
        let teamIds: string[] = [];
        
        if (showAllTeams) {
          console.log('Fetching all teams for schedule view');
          
          const { data: allTeamsData, error: allTeamsError } = await supabase
            .from('teams')
            .select('id, name')
            .order('name');
            
          if (allTeamsError) {
            console.error('Error fetching all teams:', allTeamsError);
            throw allTeamsError;
          }
          
          setClientTeams(allTeamsData || []);
          teamIds = allTeamsData?.map(t => t.id) || [];
        }
        else if (teamId) {
          console.log('Direct team ID provided:', teamId);
          teamIds = [teamId];
          
          const { data: teamData, error: teamError } = await supabase
            .from('teams')
            .select('id, name')
            .eq('id', teamId);
            
          if (teamError) {
            console.error('Error fetching team details:', teamError);
            throw teamError;
          }
          
          setClientTeams(teamData || []);
        } 
        else if (clientId) {
          console.log('Fetching teams for client:', clientId);
          
          const { data: teamsData, error: teamsError } = await supabase
            .from('client_teams')
            .select('team_id')
            .eq('client_id', clientId);
  
          if (teamsError) {
            console.error('Error fetching client teams:', teamsError);
            throw teamsError;
          }
  
          if (!teamsData || teamsData.length === 0) {
            console.log('No teams found for client');
            setIsLoading(false);
            return;
          }
  
          teamIds = teamsData.map(t => t.team_id);
          console.log('Client is assigned to teams:', teamIds);
          
          const { data: teamsWithDetails, error: detailsError } = await supabase
            .from('teams')
            .select('id, name')
            .in('id', teamIds);
            
          if (detailsError) {
            console.error('Error fetching team details:', detailsError);
            throw detailsError;
          }
          
          setClientTeams(teamsWithDetails || []);
        }
        
        await fetchTeamSchedules(teamIds);
      } catch (error: any) {
        console.error('Error fetching teams:', error);
        toast({
          title: "Error loading schedules",
          description: error.message || "Failed to load team schedules",
          variant: "destructive"
        });
        setIsLoading(false);
      }
    };

    fetchClientTeams();
  }, [clientId, teamId, showAllTeams, toast]);

  useEffect(() => {
    let teamFiltered = allShifts;
    
    if (selectedTeamId !== "all") {
      const teamName = clientTeams.find(team => team.id === selectedTeamId)?.name;
      if (teamName) {
        teamFiltered = allShifts.filter(shift => 
          shift.title.includes(`[${teamName}]`)
        );
      }
    }
    
    if (selectedGuardId !== "all") {
      teamFiltered = teamFiltered.filter(shift => {
        if (shift.assigned_guards && Array.isArray(shift.assigned_guards)) {
          return shift.assigned_guards.includes(selectedGuardId);
        }
        return false;
      });
    }
    
    if (currentView === "list") {
      teamFiltered = teamFiltered.filter(shift => {
        const title = shift.title.toLowerCase();
        return !title.includes('ρεπο') && !title.includes('με');
      });
    }
    
    setFilteredShifts(teamFiltered);
  }, [selectedTeamId, selectedGuardId, allShifts, clientTeams, currentView]);

  const fetchTeamSchedules = async (teamIds: string[]) => {
    try {
      setIsLoading(true);
      
      const { data: guards, error: guardsError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, mobile_phone, email, afm, code')
        .eq('Role', 'guard');
        
      if (guardsError) {
        console.error('Error fetching guards:', guardsError);
        throw guardsError;
      }
      
      const guardsMap: Record<string, string> = {};
      const contactsMap: Record<string, GuardContact> = {};
      
      guards?.forEach(guard => {
        guardsMap[guard.id] = guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim() || 'Unknown';
        contactsMap[guard.id] = {
          id: guard.id,
          name: guardsMap[guard.id],
          mobile_phone: guard.mobile_phone,
          email: guard.email,
          afm: guard.afm,
          code: guard.code
        };
      });
      
      setGuardNames(guardsMap);
      setGuardContacts(contactsMap);
      console.log('Fetched guard names for', Object.keys(guardsMap).length, 'guards');
      
      const allTeamShifts: CalendarEvent[] = [];
      
      for (const teamId of teamIds) {
        console.log('Fetching schedules for team:', teamId);
        const { data: schedules, error: schedulesError } = await supabase
          .from('team_schedules')
          .select('*')
          .eq('team_id', teamId);
          
        if (schedulesError) {
          console.error('Error fetching team schedules:', schedulesError);
          throw schedulesError;
        }
        
        console.log('Found', schedules?.length || 0, 'schedules for team', teamId);
        
        if (schedules && schedules.length > 0) {
          const { data: teamData } = await supabase
            .from('teams')
            .select('name')
            .eq('id', teamId)
            .single();
            
          const teamName = teamData?.name || 'Unknown Team';
          
          const formattedShifts = schedules.map(schedule => {
            let guardNames: string[] = [];
            if (schedule.assigned_guards && Array.isArray(schedule.assigned_guards)) {
              guardNames = schedule.assigned_guards.map(id => guardsMap[id] || 'Unknown');
            }
            
            const formattedTitle = `[${teamName}] ${schedule.title}`;
            
            return {
              ...schedule,
              title: formattedTitle,
              color: 'primary',
              guard_names: guardNames,
              team_id: teamId,
              team_name: teamName
            };
          });
          
          allTeamShifts.push(...formattedShifts);
        }
      }
      
      console.log('Total shifts found across all teams:', allTeamShifts.length);
      setAllShifts(allTeamShifts);
      setFilteredShifts(allTeamShifts);
    } catch (error: any) {
      console.error('Error fetching team schedules:', error);
      toast({
        title: "Error loading schedules",
        description: error.message || "Failed to load team schedules",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDayClick = (date: Date) => {
    console.log('Day clicked:', date);
  };

  const handleEventClick = (event: CalendarEvent) => {
    console.log('Event clicked:', event);
    
    if (event.assigned_guards && event.assigned_guards.length > 0) {
      const guardId = event.assigned_guards[0];
      const guardContact = guardContacts[guardId];
      
      if (guardContact && guardContact.mobile_phone) {
        callGuard(guardContact);
      } else {
        toast({
          title: "Cannot call guard",
          description: "No phone number available for this guard",
          variant: "destructive"
        });
      }
    } else {
      toast({
        description: "No guards assigned to this shift",
        variant: "destructive"
      });
    }
  };

  const callGuard = (guard: GuardContact) => {
    if (!guard.mobile_phone) {
      toast({
        title: "Cannot call guard",
        description: `No phone number available for ${guard.name}`,
        variant: "destructive"
      });
      return;
    }
    
    console.log(`Calling guard: ${guard.name} at ${guard.mobile_phone}`);
    window.location.href = `tel:${guard.mobile_phone}`;
  };

  const handleTeamChange = (value: string) => {
    setSelectedTeamId(value);
    setSelectedGuardId("all");
  };

  const handleGuardSelect = (value: string) => {
    setSelectedGuardId(value);
  };

  const handleResetFilters = () => {
    setSelectedTeamId("all");
    setSelectedGuardId("all");
  };

  const handleCallGuard = (shiftGuards: string[]) => {
    if (!shiftGuards || shiftGuards.length === 0) {
      toast({
        description: "No guards assigned to this shift",
        variant: "destructive"
      });
      return;
    }
    
    const guardId = shiftGuards[0];
    const guardContact = guardContacts[guardId];
    
    if (guardContact && guardContact.mobile_phone) {
      callGuard(guardContact);
    } else {
      toast({
        title: "Cannot call guard",
        description: "No phone number available for this guard",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full max-w-sm" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (clientTeams.length === 0) {
    return (
      <Alert variant="default" className="bg-muted">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No teams assigned to this {teamId ? "guard" : "client"} yet.
        </AlertDescription>
      </Alert>
    );
  }

  if (allShifts.length === 0) {
    return (
      <Alert variant="default" className="bg-muted">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No shifts scheduled for the teams assigned to this {teamId ? "guard" : "client"}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">Teams Schedule</span>
            {(selectedTeamId !== "all" || selectedGuardId !== "all") && (
              <div className="flex gap-1">
                {selectedTeamId !== "all" && (
                  <Badge variant="secondary" className="ml-2">
                    {clientTeams.find(team => team.id === selectedTeamId)?.name}
                  </Badge>
                )}
                {selectedGuardId !== "all" && (
                  <Badge variant="outline" className="bg-primary/10">
                    {teamGuards.find(guard => guard.id === selectedGuardId)?.full_name || "Guard"}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedTeamId} onValueChange={handleTeamChange}>
                  <SelectTrigger className="w-[140px] sm:w-[180px] bg-background">
                    <SelectValue placeholder="Filter by team" />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    <SelectItem value="all">All Teams</SelectItem>
                    {clientTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <UserRound className="h-4 w-4 text-muted-foreground" />
                {guardsLoading ? (
                  <Skeleton className="h-9 w-[140px] sm:w-[180px]" />
                ) : (
                  <GuardSelect
                    guards={teamGuards}
                    selectedGuardId={selectedGuardId}
                    onSelect={handleGuardSelect}
                  />
                )}
              </div>
              
              {(selectedTeamId !== "all" || selectedGuardId !== "all") && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleResetFilters} 
                  className="h-9"
                >
                  Reset
                </Button>
              )}
            </div>
            
            <Separator orientation="vertical" className="h-8 hidden sm:block" />
            
            <Tabs value={currentView} onValueChange={(v) => setCurrentView(v as "calendar" | "list")}>
              <TabsList>
                <TabsTrigger value="calendar">Calendar</TabsTrigger>
                <TabsTrigger value="list">List</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {currentView === "list" && (
          <div className="mb-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium mb-2">Filter by date range:</h3>
              {!roles?.includes('client') && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportToExcel}
                  className="flex items-center gap-1"
                >
                  <Download className="h-4 w-4" />
                  <span>Export</span>
                </Button>
              )}
            </div>
            <DateRangePicker
              onUpdate={handleDateRangeChange}
              className="w-full"
            >
              <Button variant="outline" className="w-full justify-start text-left">
                <Calendar className="mr-2 h-4 w-4" />
                {dateRange.from && dateRange.to ? (
                  <>
                    {format(dateRange.from, "MMM dd, yyyy")} - {format(dateRange.to, "MMM dd, yyyy")}
                  </>
                ) : (
                  <span>Select date range</span>
                )}
              </Button>
            </DateRangePicker>
          </div>
        )}

        {currentView === "calendar" && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToPreviousWeek}
                className="h-8 text-xs sm:text-sm border-dashed"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only md:not-sr-only md:ml-1">Previous</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToToday}
                className="h-8 text-xs sm:text-sm whitespace-nowrap"
              >
                Today
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToNextWeek}
                className="h-8 text-xs sm:text-sm border-dashed"
              >
                <span className="sr-only md:not-sr-only md:mr-1">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-primary" />
              <span className="font-medium text-sm">
                {weekStartFormatted} - {weekEndFormatted}
              </span>
            </div>
          </div>
        )}

        {currentView === "calendar" && (
          <div className="mt-0">
            <TotalWorkCalendar
              events={filteredShifts}
              currentDate={currentDate}
              dateRange={calendarDateRange}
              guardId="all"
              handleDayClick={handleDayClick}
              handleEventClick={handleEventClick}
              defaultViewMode="week"
              showDuration={true}
            />
          </div>
        )}
        
        {currentView === "list" && (
          <div className="mt-0 rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Duration</span>
                    </div>
                  </TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Guards</TableHead>
                  {isAdmin && (
                    <TableHead className="hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        <span>Guard Code</span>
                      </div>
                    </TableHead>
                  )}
                  {isAdmin && (
                    <TableHead className="hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <FileDigit className="h-3.5 w-3.5" />
                        <span>Guard AFM</span>
                      </div>
                    </TableHead>
                  )}
                  <TableHead className="w-[80px]">Call</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredShifts
                  .sort((a, b) => fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime())
                  .map((shift) => {
                    const titleParts = shift.title.match(/\[(.*?)\] (.*)/);
                    const teamName = titleParts ? titleParts[1] : 'Unknown';
                    const actualTitle = titleParts ? titleParts[2] : shift.title;
                    
                    const firstGuardId = shift.assigned_guards?.[0] || '';
                    const guardContactInfo = guardContacts[firstGuardId];
                    
                    return (
                      <TableRow key={shift.id}>
                        <TableCell className="font-medium">
                          {format(fromLocalISOString(shift.start_date), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell>
                          {format(fromLocalISOString(shift.start_date), 'HH:mm')}
                        </TableCell>
                        <TableCell>
                          {format(fromLocalISOString(shift.end_date), 'HH:mm')}
                        </TableCell>
                        <TableCell>
                          {formatShiftDuration(shift.start_date, shift.end_date)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-primary/10">
                            {teamName}
                          </Badge>
                        </TableCell>
                        <TableCell>{actualTitle}</TableCell>
                        <TableCell>
                          {shift.guard_names && shift.guard_names.length > 0 
                            ? shift.guard_names.join(', ') 
                            : 'No guards assigned'}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="hidden md:table-cell">
                            {guardContactInfo?.code ? 
                              <span className="text-xs font-mono">{guardContactInfo.code}</span> : 
                              '—'}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="hidden md:table-cell">
                            {guardContactInfo?.afm ? 
                              <span className="text-xs font-mono">{guardContactInfo.afm}</span> : 
                              '—'}
                          </TableCell>
                        )}
                        <TableCell>
                          {shift.assigned_guards && shift.assigned_guards.length > 0 ? (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleCallGuard(shift.assigned_guards || [])}
                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              disabled
                              className="h-8 w-8 text-muted-foreground"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientScheduleView;
